<template>
  <v-menu v-model="menuIsOpen" location="bottom end" :close-on-content-click="false" min-width="260" max-width="320">
    <template v-slot:activator="{ props }">
      <!-- Sem contagem aqui: quantos itens o prazo esconde é informação do
           cabeçalho da faixa, junto do total, e não deste botão. -->
      <!-- Sem `dark`: a faixa deixou de ser um bloco de cor saturada, e o botão
           branco sumiria no fundo claro. Assim ele herda a cor do cabeçalho. -->
      <v-btn size="small" variant="text" class="text-none px-1" v-bind="props">
        <v-icon start size="small">mdi-clock-outline</v-icon>
        <span class="period-label">{{ label }}</span>
        <v-icon end size="small">mdi-menu-down</v-icon>
      </v-btn>
    </template>

    <v-card>
      <v-list density="compact">
        <v-list-item v-for="(preset, index) in presets" :key="index" @click="apply(preset)">
          <template #prepend><span class="mr-2">
            <v-icon size="small" v-if="isSelected(preset)">mdi-check</v-icon>
          </span></template>
          {{ labelOf(preset) }}
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item @click="toggleCustom">
          <template #prepend><span class="mr-2">
            <v-icon size="small" v-if="isCustom">mdi-check</v-icon>
          </span></template>
          {{ $tt('period.custom') }}
          <template #append>
            <v-icon size="small">{{ customIsOpen ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
          </template>
        </v-list-item>
      </v-list>

      <v-card-text v-if="customIsOpen" class="pt-0">
        <v-btn-toggle v-model="customMode" mandatory density="compact" class="mb-3">
          <v-btn size="small" value="last">{{ $tt('period.mode-last') }}</v-btn>
          <v-btn size="small" value="since">{{ $tt('period.mode-since') }}</v-btn>
        </v-btn-toggle>

        <v-row v-if="customMode === 'last'" no-gutters>
          <v-col cols="4" class="pr-1">
            <v-text-field type="number" min="1" density="compact" variant="outlined" hide-details
                          :label="$tt('period.amount')" v-model="customAmount"/>
          </v-col>
          <v-col cols="8">
            <v-select density="compact" variant="outlined" hide-details :items="unitOptions"
                      item-title="text" item-value="value"
                      :label="$tt('period.unit')" v-model="customUnit"/>
          </v-col>
        </v-row>
        <v-text-field v-else type="date" density="compact" variant="outlined" hide-details clearable
                      :label="$tt('period.mode-since')" v-model="customDate"/>

        <div class="text-right mt-3">
          <v-btn size="small" color="primary" :disabled="!customIsValid" @click="applyCustom">
            {{ $tt('period.apply') }}
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'
import moment from "moment";
import {FinishedPeriod, isPreset, periodKey, PeriodUnit, PRESETS} from "@/utils/finishedPeriod";

//Mapas explícitos em vez de chave montada por template string: é o que mantém o
//$tt tipado contra o en.json e faz faltar chave virar erro de compilação.
const LAST_ONE_KEY = {
  day: 'period.last-day',
  week: 'period.last-week',
  month: 'period.last-month',
  year: 'period.last-year'
} as const

const CURRENT_KEY = {
  week: 'period.this-week',
  month: 'period.this-month',
  year: 'period.this-year'
} as const

const UNIT_KEY = {
  day: 'period.unit-day',
  week: 'period.unit-week',
  month: 'period.unit-month',
  year: 'period.unit-year'
} as const

const UNITS: PeriodUnit[] = ['day', 'week', 'month', 'year']



@Component({emits: ['input']})
class FinishedPeriodMenu extends Vue {
  @Prop({type: Object, required: true}) readonly value!: FinishedPeriod

  menuIsOpen = false
  customIsOpen = false
  customMode: 'last' | 'since' = 'last'
  customAmount: string = '1'
  customUnit: PeriodUnit = 'week'
  customDate: string = ''

  mounted() {
    this.syncCustomFromValue()
  }

  /** Reabrir o menu com um período personalizado ativo tem que trazer os campos preenchidos. */
  @Watch('menuIsOpen')
  onMenuIsOpenChanged(open: boolean) {
    if (open) {
      this.syncCustomFromValue()
      this.customIsOpen = this.isCustom
    }
  }

  syncCustomFromValue() {
    const period = this.value
    if (period.kind === 'last') {
      this.customMode = 'last'
      this.customAmount = String(period.amount)
      this.customUnit = period.unit
    } else if (period.kind === 'since') {
      this.customMode = 'since'
      this.customDate = period.date
    }
  }

  /** Getter em vez de data: constante de módulo não precisa virar reativa. */
  get presets(): FinishedPeriod[] {
    return PRESETS
  }

  get isCustom(): boolean {
    return !isPreset(this.value)
  }

  get label(): string {
    return this.labelOf(this.value)
  }

  labelOf(period: FinishedPeriod): string {
    switch (period.kind) {
      case 'all':
        return this.$tt('period.all') as string
      case 'last':
        //"Uma semana" lê melhor que "Últimos 1 semanas"; só o plural passa pelo $tc.
        if (period.amount === 1) return this.$tt(LAST_ONE_KEY[period.unit]) as string
        return this.$tt('period.last-n', {
          amount: period.amount,
          unit: this.$tc(UNIT_KEY[period.unit], period.amount)
        }) as string
      case 'current':
        return this.$tt(CURRENT_KEY[period.unit]) as string
      case 'since':
        //Aqui a data é 'YYYY-MM-DD' mesmo, então o T00:00:00 evita o recuo de fuso.
        return this.$tt('period.since', {
          date: new Date(period.date + 'T00:00:00').toLocaleDateString()
        }) as string
    }
  }

  isSelected(period: FinishedPeriod): boolean {
    return periodKey(period) === periodKey(this.value)
  }

  get unitOptions() {
    return UNITS.map(unit => ({
      value: unit,
      text: this.$tc(UNIT_KEY[unit], Number(this.customAmount) || 1)
    }))
  }

  get customIsValid(): boolean {
    if (this.customMode === 'last') {
      const amount = Number(this.customAmount)
      return Number.isInteger(amount) && amount >= 1
    }
    return !!this.customDate && moment(this.customDate, 'YYYY-MM-DD', true).isValid()
  }

  toggleCustom() {
    this.customIsOpen = !this.customIsOpen
  }

  applyCustom() {
    if (!this.customIsValid) return
    const period: FinishedPeriod = this.customMode === 'last'
        ? {kind: 'last', amount: Number(this.customAmount), unit: this.customUnit}
        : {kind: 'since', date: this.customDate}
    this.apply(period)
  }

  apply(period: FinishedPeriod) {
    this.$emit('input', period)
    this.customIsOpen = false
    this.menuIsOpen = false
  }
}

export default toNative(FinishedPeriodMenu)
</script>

<style scoped>
.period-label {
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
